import React, { useState, useEffect } from 'react';
import { X, FileText, MapPin } from 'lucide-react';
import { collection, addDoc, doc, setDoc } from "firebase/firestore";

// Shared Styles
const modalOverlayStyle = { position: 'fixed', top:0, left:0, right:0, bottom:0, backgroundColor: 'rgba(0,0,0,0.5)', display:'flex', justifyContent:'center', alignItems:'center', zIndex: 1000 };
const modalContentStyle = { backgroundColor: 'white', width: '900px', maxWidth:'95%', borderRadius: '8px', padding: '20px', display:'flex', flexDirection:'column', position: 'relative' };
const rowStyle = { display: 'flex', gap: '15px', marginBottom: '15px' };
const sectionHeaderStyle = { margin: '20px 0 10px 0', borderBottom:'1px solid #eee', paddingBottom:5, color:'#374151', fontSize: '14px', fontWeight:'bold' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };
const btnPrimaryStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };

export const NewCustomerModal = ({ show, onClose, onSave, db, isMobile, customerData = null, isEditingAllowed = true }) => {
  const [newCust, setNewCust] = useState(customerData ? {
    type: customerData.type || 'business', 
    salutation: customerData.primary_contact?.salutation || 'Mr.', 
    firstName: customerData.primary_contact?.first_name || '', 
    lastName: customerData.primary_contact?.last_name || '',
    companyName: customerData.company_name || '', 
    displayName: customerData.display_name || '', 
    email: customerData.email || '', 
    workPhone: customerData.work_phone || '', 
    mobile: customerData.mobile || '',
    gstin: customerData.gstin || '', 
    pan: customerData.pan || '', 
    currency: customerData.currency || 'INR', 
    paymentTerms: customerData.payment_terms || 'Due on Receipt',
  } : {
    type: 'business', salutation: 'Mr.', firstName: '', lastName: '',
    companyName: '', displayName: '', email: '', workPhone: '', mobile: '',
    gstin: '', pan: '', currency: 'INR', paymentTerms: 'Due on Receipt',
  });

  // Reset newCust state when customerData changes
  useEffect(() => {
    if (customerData) {
      setNewCust({
        type: customerData.type || 'business', 
        salutation: customerData.primary_contact?.salutation || 'Mr.', 
        firstName: customerData.primary_contact?.first_name || '', 
        lastName: customerData.primary_contact?.last_name || '',
        companyName: customerData.company_name || '', 
        displayName: customerData.display_name || '', 
        email: customerData.email || '', 
        workPhone: customerData.work_phone || '', 
        mobile: customerData.mobile || '',
        gstin: customerData.gstin || '', 
        pan: customerData.pan || '', 
        currency: customerData.currency || 'INR', 
        paymentTerms: customerData.payment_terms || 'Due on Receipt',
      });
    } else {
      setNewCust({
        type: 'business', salutation: 'Mr.', firstName: '', lastName: '',
        companyName: '', displayName: '', email: '', workPhone: '', mobile: '',
        gstin: '', pan: '', currency: 'INR', paymentTerms: 'Due on Receipt',
      });
    }
  }, [customerData]);

  if (!show) return null;

  const handleModalChange = (e) => setNewCust({ ...newCust, [e.target.name]: e.target.value });
  // Removed handleModalNested and copyBillingToShippingModal as address fields are no longer in this modal's scope

  const saveNewCustomer = async () => {
    if (!newCust.displayName) return alert("Display Name is required.");
    
    // Construct customerObj without addresses initially
    const customerObj = {
      company_name: newCust.companyName,
      display_name: newCust.displayName,
      email: newCust.email,
      work_phone: newCust.workPhone,
      mobile: newCust.mobile,
      gstin: newCust.gstin,
      pan: newCust.pan,
      payment_terms: newCust.paymentTerms,
      primary_contact: { salutation: newCust.salutation, first_name: newCust.firstName, last_name: newCust.lastName },
      // If editing an existing customer, explicitly retain their addresses from customerData
      ...(customerData && customerData.addresses ? { addresses: customerData.addresses } : {}),
    };

    try {
      let savedCust;
      if (customerData) {
        // Update existing customer
        const customerRef = doc(db, "customers", customerData.id);
        await setDoc(customerRef, customerObj, { merge: true });
        savedCust = { id: customerData.id, ...customerObj };
      } else {
        // Add new customer
        const docRef = await addDoc(collection(db, "customers"), customerObj);
        savedCust = { id: docRef.id, ...customerObj };
      }
      onSave(savedCust);
      onClose();
    } catch (e) { alert("Error saving customer"); }
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', paddingBottom: '15px', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>{customerData ? 'Edit Customer' : 'New Customer'}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer' }}><X size={24} /></button>
        </div>

        <div style={{ overflowY: 'auto', maxHeight: '70vh', paddingRight: '10px' }}>
          
          {/* SECTION: BASIC INFO */}
          <h4 style={sectionHeaderStyle}>Customer Details</h4>
          <div style={{ ...rowStyle, flexDirection: isMobile ? 'column' : 'row' }}>
             <div style={{flex:1}}>
               <label style={labelStyle}>Primary Contact</label>
               <div style={{display:'flex', gap:10}}>
                  <select name="salutation" value={newCust.salutation} onChange={handleModalChange} style={{...inputStyle, width:'80px'}} disabled={!isEditingAllowed}>
                    <option>Mr.</option><option>Ms.</option><option>Mrs.</option><option>Dr.</option>
                  </select>
                  <input name="firstName" placeholder="First Name" value={newCust.firstName} onChange={handleModalChange} style={inputStyle} disabled={!isEditingAllowed} />
                  <input name="lastName" placeholder="Last Name" value={newCust.lastName} onChange={handleModalChange} style={inputStyle} disabled={!isEditingAllowed} />
               </div>
             </div>
          </div>
          <div style={{ ...rowStyle, flexDirection: isMobile ? 'column' : 'row' }}>
             <div style={{flex:1}}><label style={labelStyle}>Company Name</label><input name="companyName" value={newCust.companyName} onChange={handleModalChange} style={inputStyle} disabled={!isEditingAllowed} /></div>
             <div style={{flex:1}}><label style={labelStyle}>Display Name *</label><input name="displayName" value={newCust.displayName} onChange={handleModalChange} style={inputStyle} disabled={!isEditingAllowed} /></div>
          </div>
          <div style={{ ...rowStyle, flexDirection: isMobile ? 'column' : 'row' }}>
             <div style={{flex:1}}><label style={labelStyle}>Email</label><input name="email" value={newCust.email} onChange={handleModalChange} style={inputStyle} disabled={!isEditingAllowed} /></div>
             <div style={{flex:0.5}}><label style={labelStyle}>Work Phone</label><input name="workPhone" value={newCust.workPhone} onChange={handleModalChange} style={inputStyle} disabled={!isEditingAllowed} /></div>
             <div style={{flex:0.5}}><label style={labelStyle}>Mobile</label><input name="mobile" value={newCust.mobile} onChange={handleModalChange} style={inputStyle} disabled={!isEditingAllowed} /></div>
          </div>

          

          {/* SECTION: OTHER INFO */}
          <h4 style={sectionHeaderStyle}>Other Details</h4>
          <div style={{ ...rowStyle, flexDirection: isMobile ? 'column' : 'row' }}>
             <div style={{flex:1}}><label style={labelStyle}>GSTIN</label><input name="gstin" value={newCust.gstin} onChange={handleModalChange} style={inputStyle} disabled={!isEditingAllowed} /></div>
             <div style={{flex:1}}><label style={labelStyle}>PAN</label><input name="pan" value={newCust.pan} onChange={handleModalChange} style={inputStyle} disabled={!isEditingAllowed} /></div>
             <div style={{flex:1}}>
               <label style={labelStyle}>Payment Terms</label>
               <select name="paymentTerms" value={newCust.paymentTerms} onChange={handleModalChange} style={inputStyle} disabled={!isEditingAllowed}>
                 <option>Due on Receipt</option><option>Advance</option><option>Prior to Dispatch</option><option>Net 15</option><option>Net 30</option><option>Net 45</option><option>Net 60</option>
               </select>
             </div>
          </div>
        </div>
        
        <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 20, paddingTop: 15, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
           <button onClick={onClose} style={{ padding: '8px 15px', border: '1px solid #d1d5db', background: 'white', borderRadius: 5, cursor:'pointer' }}>Cancel</button>
           {isEditingAllowed && <button onClick={saveNewCustomer} style={btnPrimaryStyle}>Save Customer</button>}
        </div>
      </div>
    </div>
  );
};

export const AddAddressModal = ({ show, onClose, onSave, db, customer, addressType, isMobile, addressData = null, isEditingAllowed = true }) => {
  const [useForBoth, setUseForBoth] = useState(false);
  const [newAddress, setNewAddress] = useState(addressData ? {
    attention: addressData.attention || '',
    country: addressData.country || 'India',
    address: addressData.address || '',
    city: addressData.city || '',
    state: addressData.state || '',
    pincode: addressData.pincode || '',
    phone: addressData.phone || '',
    email: addressData.email || '',
    address_type: addressData.address_type || (addressType === 'billing' ? 'Billing' : 'Shipping'),
    id: addressData.id // Preserve ID for editing
  } : {
    attention: '', country: 'India', address: '', city: '', state: '', pincode: '', phone: '', email: '',
    address_type: addressType === 'billing' ? 'Billing' : 'Shipping',
  });

  // Reset newAddress state when addressData or addressType changes
  useEffect(() => {
    setUseForBoth(false);
    if (addressData) {
      setNewAddress({
        attention: addressData.attention || '',
        country: addressData.country || 'India',
        address: addressData.address || '',
        city: addressData.city || '',
        state: addressData.state || '',
        pincode: addressData.pincode || '',
        phone: addressData.phone || '',
        email: addressData.email || '',
        address_type: addressData.address_type || (addressType === 'billing' ? 'Billing' : 'Shipping'),
        id: addressData.id
      });
    } else {
      setNewAddress({
        attention: '', country: 'India', address: '', city: '', state: '', pincode: '', phone: '', email: '',
        address_type: addressType === 'billing' ? 'Billing' : 'Shipping',
      });
    }
  }, [addressData, addressType, show]);

  if (!show) return null;

  const handleNewAddressChange = (e) => setNewAddress({ ...newAddress, [e.target.name]: e.target.value });

  const saveNewAddress = async () => {
    if (!newAddress.address || !newAddress.city) return alert("Address and City are required.");
    
    let updatedAddresses;
    let finalAddressToSave;
    let otherAddressToSave = null;

    if (addressData) {
      // Editing existing address
      finalAddressToSave = { ...newAddress }; // ID is already in newAddress
      updatedAddresses = (customer.addresses || []).map(addr => 
        addr.id === finalAddressToSave.id ? finalAddressToSave : addr
      );
    } else {
      // Adding new address
      finalAddressToSave = {
        ...newAddress,
        id: Date.now().toString(),
      };
      updatedAddresses = [...(customer.addresses || []), finalAddressToSave];

      if (useForBoth) {
        otherAddressToSave = {
          ...newAddress,
          id: (Date.now() + 1).toString(),
          address_type: addressType === 'billing' ? 'Shipping' : 'Billing'
        };
        updatedAddresses.push(otherAddressToSave);
      }
    }
    
    const customerRef = doc(db, "customers", customer.id);
    
    try {
        await setDoc(customerRef, { addresses: updatedAddresses }, { merge: true });
        
        if (otherAddressToSave) {
          onSave(finalAddressToSave, updatedAddresses);
          onSave(otherAddressToSave, updatedAddresses);
        } else {
          onSave(finalAddressToSave, updatedAddresses);
        }
        
        onClose();
    } catch (e) { alert("Error saving address: " + e.message); }
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={{...modalContentStyle, width: isMobile ? '95%' : '500px'}}>
        <h3 style={{ margin: 0 }}>{addressData ? `Edit ${addressType.charAt(0).toUpperCase() + addressType.slice(1)} Address` : `Add New ${addressType.charAt(0).toUpperCase() + addressType.slice(1)} Address`}</h3>
        <button onClick={onClose} style={{ position:'absolute', top:20, right:20, border: 'none', background: 'none', cursor: 'pointer' }}><X size={24} /></button>
        <div style={{paddingTop: '20px'}}>
           {!addressData && (
             <div style={{ marginBottom: '15px' }}>
               <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', color: '#374151' }}>
                 <input 
                   type="checkbox" 
                   checked={useForBoth} 
                   onChange={(e) => setUseForBoth(e.target.checked)} 
                 />
                 Use this address for both Billing and Shipping
               </label>
             </div>
           )}
           <input placeholder="Attention Person" name="attention" value={newAddress.attention} onChange={handleNewAddressChange} style={{...inputStyle, marginBottom:'10px'}} disabled={!isEditingAllowed} />
           <textarea placeholder="Street Address" name="address" value={newAddress.address} onChange={handleNewAddressChange} style={{...inputStyle, marginBottom:'10px', resize:'vertical'}} disabled={!isEditingAllowed} />
           <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px'}}>
              <input placeholder="City *" name="city" value={newAddress.city} onChange={handleNewAddressChange} style={inputStyle} disabled={!isEditingAllowed} />
              <input placeholder="State" name="state" value={newAddress.state} onChange={handleNewAddressChange} style={inputStyle} disabled={!isEditingAllowed} />
              <input placeholder="Pin Code" name="pincode" value={newAddress.pincode} onChange={handleNewAddressChange} style={inputStyle} disabled={!isEditingAllowed} />
              <input placeholder="Phone" name="phone" value={newAddress.phone} onChange={handleNewAddressChange} style={inputStyle} disabled={!isEditingAllowed} />
              <input placeholder="Email" name="email" value={newAddress.email} onChange={handleNewAddressChange} style={{...inputStyle, gridColumn: 'span 2'}} disabled={!isEditingAllowed} />
           </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop:15 }}>
           <button onClick={onClose} style={{ padding: '8px 15px', border: '1px solid #d1d5db', background: 'white', borderRadius: 5 }}>Cancel</button>
           {isEditingAllowed && <button onClick={saveNewAddress} style={btnPrimaryStyle}>Save Address</button>}
        </div>
      </div>
    </div>
  );
};